import cn from 'classnames';
import * as React from 'react';

import * as styles from './OfferCard.css';
import { Labels } from '../Labels';
import { AboutBuilding } from './components/AboutBuilding';
import { Address } from './components/Address';
import { Branding } from './components/Branding';
import { Info } from './components/Info';
import { Rating } from './components/Rating';
import { Thumbnails } from './components/Thumbnails';
import { UndergroundStations } from './components/UndergroundStations';
import { ETestId } from '../../constants';
import { ButtonsContainer } from '../../containers/Buttons';
import { CarouselContainer } from '../../containers/Carousel';
import { IDailyRentOfferForMobileSite } from '../../repositories/dailyrent/entities/daily_rent_offers_for_web/DailyRentOfferForMobileSite';

interface IOfferCard {
  offer: IDailyRentOfferForMobileSite;
  hasDates: boolean;
  showBrandingBlock: boolean;
  showThumbnails?: boolean;
  carouselControls?: React.ReactNode;
  offerLink: string;
  isSingle: boolean;

  onCardClick(): void;
  onMoreClick(): void;
  onBookClick(): void;
  onCallClick(): void;
  onChatClick(): void;
  onSlideChange(): void;
  onBuildingClick(): void;
}

export const OfferCard = ({
  offer,
  hasDates,
  showBrandingBlock,
  showThumbnails = true,
  carouselControls,
  isSingle,
  offerLink,
  onCardClick,
  onMoreClick,
  onBookClick,
  onCallClick,
  onChatClick,
  onSlideChange,
  onBuildingClick,
}: IOfferCard) => {
  const hasRatingOrReviews = Boolean(offer.rating || offer.reviewsCount);
  const carouselRoundClass =
    showThumbnails && offer.media?.length > 1 ? styles['carousel-round-top'] : styles['carousel-round-full'];

  return (
    <div
      className={cn(styles['root'], {
        [styles['root_reduced-padding']]: isSingle && showBrandingBlock,
        [styles['root_enhanced-padding']]: isSingle && !showBrandingBlock,
      })}
      data-testid={ETestId.OfferCard}
      data-partner={offer.isCianPartner}
    >
      {showBrandingBlock && offer.author && (
        <a
          className={styles['wrapper-link']}
          href={offerLink}
          target="_blank"
          rel="noreferrer"
          onClick={onCardClick}
          data-testid={ETestId.OfferCardAuthorBranding}
          data-linkname="link_to_card"
        >
          <Branding {...offer.author} />
        </a>
      )}
      <CarouselContainer
        className={carouselRoundClass}
        offer={offer}
        offerLink={offerLink}
        onSlideChange={onSlideChange}
        onClick={onCardClick}
        extraControls={
          <div className={styles['modal-controls']}>
            <div className={styles['button-container']}>{carouselControls}</div>
          </div>
        }
      />

      {/* draggable=false: иначе зажатие мышью на лейблах запускает нативный drag ссылки и ломает drag-scroll (useDragScroll в Labels) */}
      <a
        className={styles['wrapper-link']}
        draggable={false}
        href={offerLink}
        target="_blank"
        rel="noreferrer"
        data-linkname="link_to_card"
        onClick={onCardClick}
      >
        {showThumbnails && (
          <div className={styles['thumbnails-container']}>
            <Thumbnails media={offer.media} />
          </div>
        )}

        <Labels className={styles['labels']} labels={offer.photoLabels} isScrollEnabled />
        <div className={styles['info']}>
          <Info {...offer} hasDates={hasDates} hasRating={hasRatingOrReviews} />
        </div>
        <Rating offer={offer} />

        <div className={styles['underground']}>
          <UndergroundStations {...offer} />
        </div>

        <div className={styles['address']}>
          <Address address={offer.geo.address} />
        </div>
        <AboutBuilding
          name={offer.geo?.title?.name}
          link={offer.geo?.title?.link}
          linkTarget="_blank"
          onLinkClick={onBuildingClick}
          similarOffersCount={offer.building?.similarOffersCount}
        />
      </a>
      <div data-testid="OfferCardButtonContainer">
        <ButtonsContainer
          offer={offer}
          offerLink={offerLink}
          onMoreClick={onMoreClick}
          onBookClick={onBookClick}
          onCallClick={onCallClick}
          onChatClick={onChatClick}
        />
      </div>
    </div>
  );
};
